import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

const normalizeText = (value) => {
  if (!value) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
};

const MODULE_MIGRATION_MAP = {
  'Gestión Escolar': 'Control Escolar',
  'Gestión de Estudiantes': 'Perfil Académico',
  'Gestión Financiera': 'Finanzas',
  'Administración': 'Administración del Sistema',
  'Catálogos': 'Administración del Sistema',
  'Reportes': 'Otros',
};

const normalizeModule = (modulo) => MODULE_MIGRATION_MAP[modulo] || modulo;

const getModuleCandidates = (modulo) => {
  if (!modulo) return [];

  const candidates = new Set();
  const add = (value) => {
    if (typeof value === 'string' && value.trim()) {
      candidates.add(value);
    }
  };

  const normalized = normalizeModule(modulo);
  add(modulo);
  add(normalized);

  Object.entries(MODULE_MIGRATION_MAP).forEach(([legacyModule, canonicalModule]) => {
    if (
      modulo === legacyModule ||
      modulo === canonicalModule ||
      normalized === legacyModule ||
      normalized === canonicalModule
    ) {
      add(legacyModule);
      add(canonicalModule);
    }
  });

  return Array.from(candidates);
};

const resolvePermissionModule = (permisos, modulo) => {
  const candidates = getModuleCandidates(modulo);
  for (const candidate of candidates) {
    if (permisos[candidate]) {
      return candidate;
    }
  }
  return normalizeModule(modulo);
};

export const getUserRoleNames = (user) => {
  if (!user) return [];

  const roleSet = new Set();
  const pushRole = (value) => {
    const normalized = normalizeText(value);
    if (normalized) roleSet.add(normalized);
  };

  pushRole(user.rol_nombre);
  pushRole(user.nombre_rol);

  if (Array.isArray(user.roles_lista)) {
    user.roles_lista.forEach((roleItem) => {
      if (typeof roleItem === 'string') {
        pushRole(roleItem);
        return;
      }
      if (roleItem && typeof roleItem === 'object') {
        pushRole(roleItem.nombre_rol);
        pushRole(roleItem.rol_nombre);
      }
    });
  }

  return Array.from(roleSet);
};

export const hasStudentRole = (user) => {
  const roleNames = getUserRoleNames(user);
  return roleNames.some((role) => role === 'ESTUDIANTE' || role.includes('ESTUDIANTE'));
};

export const hasAdminRole = (user) => {
  const roleNames = getUserRoleNames(user);
  return roleNames.some((role) => role.includes('ADMIN'));
};


export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('authToken') || null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
  const apiUrl = apiBase;

  // --- INACTIVITY LOGOUT ---
  React.useEffect(() => {
    let timeout;
    const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutos
    const resetTimer = () => {
      if (timeout) clearTimeout(timeout);
      if (token && user) {
        timeout = setTimeout(() => {
          logout();
        }, INACTIVITY_LIMIT);
      }
    };
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      if (timeout) clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
    // eslint-disable-next-line
  }, [token, user]);

  const saveAuth = (t, u) => {
    setToken(t);
    setUser(u);
    localStorage.setItem('authToken', t || '');
    localStorage.setItem('currentUser', u ? JSON.stringify(u) : 'null');
  };

  // Nuevo login usando JWT estándar de Django
  const login = useCallback(async (email, password, institucion_id = null) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.detail || 'Tu correo o tu contraseña son incorrectos';
        const err = new Error(msg);
        err.status = res.status;
        throw err;
      }

      // Guardar token inmediatamente para permitir navegar más rápido
      setToken(data.access);
      localStorage.setItem('authToken', data.access);

      // Establecer un usuario mínimo de forma inmediata (usuario de auth_user sin perfil de sys_usuarios aún)
      const minimalUser = { email, isSysUser: false };
      setUser(minimalUser);
      localStorage.setItem('currentUser', JSON.stringify(minimalUser));

      // Cargar el perfil completo en segundo plano (no bloquea el login)
      (async () => {
        try {
          const profileRes = await fetch(`${apiUrl}/sistema/usuarios/me/`, {
            headers: { 'Authorization': `Bearer ${data.access}` }
          });
          if (!profileRes.ok) return;
          const userData = await profileRes.json();
          const sysUser = { ...userData, isSysUser: true };
          setUser(sysUser);
          localStorage.setItem('currentUser', JSON.stringify(sysUser));
        } catch {
          // Si falla la carga del perfil, dejamos el usuario mínimo
        }
      })();

      // Devolvemos al menos el usuario mínimo para continuar el flujo
      return minimalUser;
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const logout = useCallback(async () => {
    const t = localStorage.getItem('authToken');
    try {
      if (t) {
        await fetch(`${apiUrl}/auth/logout`, { method: 'POST', headers: { 'Authorization': `Token ${t}` } });
      }
    } catch (_) { /* ignore */ }
    saveAuth(null, null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
  }, [apiUrl]);

  // Refrescar perfil usando JWT
  const refreshMe = useCallback(async () => {
    const t = localStorage.getItem('authToken');
    if (!t) return null;
    try {
      const res = await fetch(`${apiUrl}/sistema/usuarios/me/`, { headers: { 'Authorization': `Bearer ${t}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { throw new Error('Token inválido'); }
      if (data) {
        saveAuth(t, data);
        return data;
      }
    } catch (_) {
      saveAuth(null, null);
    }
    return null;
  }, [apiUrl]);

  // Cambiar contraseña (primer acceso, requiere sesión)
  const cambiarContrasena = useCallback(async (nuevaContrasena, confirmarContrasena) => {
    const t = localStorage.getItem('authToken');
    if (!t) throw new Error('No hay sesión activa');
    
    const res = await fetch(`${apiUrl}/sistema/usuarios/cambiar-contrasena/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${t}`
      },
      body: JSON.stringify({
        nueva_contrasena: nuevaContrasena,
        confirmar_contrasena: confirmarContrasena
      })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || 'Error al cambiar la contraseña');
    }
    
    // Actualizar el usuario en el estado para reflejar que ya no es primer acceso
    if (user) {
      const updatedUser = { ...user, primer_acceso: false };
      setUser(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
    
    return data;
  }, [apiUrl, user]);

  // Confirmar reset de contraseña (desde enlace del email, sin sesión)
  const confirmarResetContrasena = useCallback(async (uid, token, nuevaContrasena, confirmarContrasena) => {
    const res = await fetch(`${apiUrl}/sistema/usuarios/confirmar-reset-contrasena/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid,
        token,
        nueva_contrasena: nuevaContrasena,
        confirmar_contrasena: confirmarContrasena
      })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Error al restablecer la contraseña');
    }
    return data;
  }, [apiUrl]);

  useEffect(() => { if (!user && token) { refreshMe(); } }, []);

  const value = useMemo(() => ({ token, user, loading, login, logout, refreshMe, cambiarContrasena, confirmarResetContrasena }), [token, user, loading, login, logout, refreshMe, cambiarContrasena, confirmarResetContrasena]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function ProtectedRoute({ element, allowFirstAccess = false, modulo = null }) {
  const { user, token } = useAuth();
  const location = useLocation();

  // Si viene del enlace del email con uid+token, permitir acceso sin sesión
  const searchParams = new URLSearchParams(location.search);
  const hasResetParams = searchParams.get('uid') && searchParams.get('token');
  if (allowFirstAccess && hasResetParams) {
    return React.isValidElement(element) ? element : null;
  }

  if (user && token) {
    // Si es estudiante, redirigir siempre al portal de estudiantes
    if (hasStudentRole(user)) {
      return <Navigate to="/" replace />;
    }
    // Si es primer acceso y no está en la pantalla de restablecer contraseña, redirigir
    if (user.primer_acceso && !allowFirstAccess) {
      return <Navigate to="/restablecer-contrasena" replace />;
    }
    // Validar permiso de módulo si se especifica
    if (modulo) {
      const permisos = user.permisos || {};
      if (Object.keys(permisos).length === 0) {
        if (!hasAdminRole(user)) {
          return <Navigate to="/inicio" replace />;
        }
      } else {
        const moduleKey = resolvePermissionModule(permisos, modulo);
        const tienePermiso = permisos[moduleKey]?.puede_leer === true;
        if (!tienePermiso) {
          return <Navigate to="/inicio" replace />;
        }
      }
    }
    if (React.isValidElement(element)) {
      return element;
    } else if (typeof element === 'function') {
      const Comp = element;
      return <Comp />;
    } else {
      return null;
    }
  }
  return <Navigate to="/login" replace />;
}
